"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";

const SESSION_KEY = "ed-ledger-gate-dismissed";

function getFocusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const q = root.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
  );
  return [...q].filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
}

/**
 * After ~4.5 viewport heights, opens an accessible dialog: focus trap, Escape, scroll lock,
 * restores focus on close. Once dismissed — sessionStorage.
 */
export function BlogScrollSubscribeGate() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const prevFocus = useRef<HTMLElement | null>(null);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!pathname?.startsWith("/blog")) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {
      /* ignore */
    }

    let triggered = false;
    const threshold = () => Math.min(window.innerHeight * 4.5, 3200);

    const onScroll = () => {
      if (triggered) return;
      if (window.scrollY >= threshold()) {
        triggered = true;
        setOpen(true);
        window.removeEventListener("scroll", onScroll, { capture: true } as AddEventListenerOptions);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll, { capture: true } as AddEventListenerOptions);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    prevFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const card = cardRef.current;
    const focusables = getFocusable(card);
    const first = focusables[0];
    first?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== "Tab" || focusables.length === 0) return;
      const idx = focusables.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey) {
        if (idx <= 0) {
          e.preventDefault();
          focusables[focusables.length - 1]?.focus();
        }
      } else if (idx === focusables.length - 1) {
        e.preventDefault();
        focusables[0]?.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus.current?.focus({ preventScroll: true });
    };
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div className="ed-blog-gate" role="presentation">
      <div className="ed-blog-gate__backdrop" onClick={dismiss} aria-hidden />
      <div
        ref={cardRef}
        className="ed-blog-gate__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button type="button" className="ed-blog-gate__close" onClick={dismiss} aria-label="Dismiss dialog">
          ×
        </button>
        <p className="ed-blog-gate__kicker">ExpiryDesk · Insights</p>
        <h2 id={titleId} className="ed-blog-gate__title">
          Want deeper analysis &amp; the Weekly Ledger?
        </h2>
        <p className="ed-blog-gate__body">
          Register for sharper write-ups, data-led breakdowns, and our Thursday briefing — built for operators who care about cash on the shelf.
        </p>
        <div className="ed-blog-gate__actions">
          <Link href="/signup" className="ed-blog-gate__primary">
            Create free account
          </Link>
          <Link href="/blog#weekly-ledger-heading" className="ed-blog-gate__secondary" onClick={dismiss}>
            Newsletter only
          </Link>
        </div>
      </div>
    </div>
  );
}
